<template>
    <v-app>

        <!-- header component -->
        <header-card-public ref="refHeader" current_page="sign-up"></header-card-public>
        
        <v-content class="content-container">
            <v-layout fill-height align-center row wrap>
                <v-flex xs12 sm12 :class="isMobile?'form-container-mobile':'form-container'">
                    <v-form @submit.prevent="executeRecaptcha" ref="form">

                        <v-card dense :class="isMobile? 'elevation-2 mt-0' : 'elevation-2 mt-50'">
                            <!-- TOOLBAR: Height slightly extended to 48px to balance the subtitle spacing -->
                            <v-toolbar flat height="48px" class="grey lighten-4 border-bottom">
                                <span class="font-weight-bold subheading grey--text text--darken-4">
                                    Book Your Free Demo
                                </span>
                                <v-spacer></v-spacer>
                                <!-- Desktop Partner Section -->
                                <v-chip 
                                    v-if="partnerData && !isMobile" 
                                    small 
                                    outlined 
                                    color="primary"
                                    class="font-weight-medium"
                                >
                                    <v-icon left small>handshake</v-icon>
                                    Partner Referral: {{ partnerData.firstName }} {{ partnerData.lastName }}
                                </v-chip>
                            </v-toolbar>
                            
                            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="ma-4">
                                {{ errorMessage }}
                            </v-alert>
                            
                            <!-- Added uniform internal padding to clean up the content stream -->
                            <v-card-text class="pa-4">

                                <!-- Mobile Partner Section: Wrapped inside a clean system alert banner -->
                                <v-layout row wrap v-if="partnerData && isMobile" class="mb-4">
                                    <v-flex xs12>
                                        <v-alert :value="true" color="info" outline icon="handshake" class="ma-0 py-2 body-2">
                                            Partner Referral: <b>{{ partnerData.firstName }} {{ partnerData.lastName }}</b>
                                        </v-alert>
                                    </v-flex>
                                </v-layout>

                                <!-- Header Introduction Text Block -->
                                <v-layout row wrap>
                                    <v-flex xs12>
                                        <p v-html="intro.line1" class="body-1 text--primary mb-3 text-justify"></p> 
                                        <p v-html="intro.line2" class="body-2 grey--text text--darken-2 mb-4 text-justify"></p>
                                    </v-flex>
                                </v-layout>

                                <!-- company name and email -->
                                <v-layout row wrap>
                                    <v-flex xs12 sm8 :class="isMobile?'content-mobile':'content1'">
                                        <v-text-field label="Company name"
                                            prepend-inner-icon="store"
                                            v-model="company"
                                            :rules="[rules.required]"
                                            :disabled="disabled"
                                            maxlength="100"
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm4 :class="isMobile?'content-mobile':'content2'">
                                        <v-text-field label="Email"
                                            prepend-inner-icon="email"
                                            v-model="email"
                                            :rules="[rules.required, rules.email]"
                                            :disabled="disabled"
                                            maxlength="100"
                                            hint="Login details will be sent to this email."
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                </v-layout>

                                <!-- first name, last name, position -->
                                <v-layout row wrap>
                                    <v-flex xs12 sm4 :class="isMobile?'content-mobile':'content1'">
                                        <v-text-field label="First Name"
                                            prepend-inner-icon="person"
                                            v-model="firstName"
                                            :rules="[rules.required]"
                                            :disabled="disabled"
                                            maxlength="100"
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm4 :class="isMobile?'content-mobile':'content1'">
                                        <v-text-field label="Last Name"
                                            prepend-inner-icon="person"
                                            v-model="lastName"
                                            :rules="[rules.required]"
                                            :disabled="disabled"
                                            maxlength="100"
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm4 :class="isMobile?'content-mobile':'content2'">
                                        <v-text-field label="Position"
                                            prepend-inner-icon="badge"
                                            v-model="position"
                                            :rules="[rules.required]"
                                            :disabled="disabled"
                                            maxlength="50"
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                </v-layout>

                                <!-- industries and employees -->
                                <v-layout row wrap>
                                    <v-flex xs12 sm8 :class="isMobile?'content-mobile':'content1'">
                                        <v-autocomplete
                                            v-model="industry"
                                            prepend-inner-icon="domain"
                                            :disabled="disabled"
                                            :items="industry_list"
                                            :rules="[rules.required]"
                                            chips
                                            color="primary lighten-2"
                                            label="Industry"
                                            item-text="name"
                                            item-value="name"
                                            >
                                            <template v-slot:selection="data">
                                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                                    {{ data.item.name }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="data">
                                                <template v-if="typeof data.item !== 'object'">
                                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                                </template>
                                                <template v-else>
                                                    <v-list-tile-content>
                                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                                    </v-list-tile-content>
                                                </template>
                                            </template>
                                        </v-autocomplete>  
                                    </v-flex>
                                    <v-flex xs12 sm4 :class="isMobile?'content-mobile mt-10':'content2 mt-10'">
                                        <v-text-field label="Number Of Employees"
                                            prepend-inner-icon="groups"
                                            v-model="employeesCount"
                                            :rules="[rules.required_number]"
                                            :disabled="disabled"
                                            type="number"
                                            hint="Estimated number of employees in your company."
                                            @keyup="error=false"></v-text-field>
                                    </v-flex>
                                </v-layout>

                                <!-- lead sources -->
                                <v-layout row wrap>
                                    <v-flex xs12 sm8 :class="isMobile?'content-mobile':'content1'">
                                        <v-autocomplete
                                            v-model="leadSource"
                                            prepend-inner-icon="campaign"
                                            :disabled="disabled"
                                            :items="lead_sources"
                                            :rules="[rules.required]"
                                            chips
                                            color="primary lighten-2"
                                            label="How did you hear about us?"
                                            item-text="name"
                                            item-value="name"
                                            >
                                            <template v-slot:selection="data">
                                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                                    {{ data.item.name }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="data">
                                                <template v-if="typeof data.item !== 'object'">
                                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                                </template>
                                                <template v-else>
                                                    <v-list-tile-content>
                                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                                    </v-list-tile-content>
                                                </template>
                                            </template>
                                        </v-autocomplete> 
                                    </v-flex>

                                    <v-flex xs12 sm4 v-if="!isMobile">
                                        <v-btn type="submit" large block :loading="loading" class="primary rounded-4 mt-15 font-weight-bold">
                                            Book Now
                                        </v-btn>
                                    </v-flex>
                                </v-layout>

                            </v-card-text>  
                            
                            <v-divider></v-divider>
                            
                            <v-card-actions v-if="isMobile">
                                <v-btn type="submit" large block :loading="loading" class="primary rounded-4 font-weight-bold">
                                    Book Now
                                </v-btn>
                            </v-card-actions>

                        </v-card>
                    </v-form>
                </v-flex>
            </v-layout>
        </v-content>

        <!-- FOOTER -->
        <v-footer dark height="auto">
            <footer-card></footer-card>
        </v-footer>
        
        <v-dialog v-model="dialogConfirmation" width="600" persistent>   
            <sign-up-confirm ref="signUpConfirmationRef" :dialogConfirmation.sync="dialogConfirmation" @send="goToHome"></sign-up-confirm>
        </v-dialog>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>
    </v-app>
</template>
<script>

import registrationApi from '@/api/registration'
import '@/assets/css/public-FaceAI.css' 
import UAParser from "ua-parser-js"

export default {
    name: 'SignUp',
    metaInfo: {
        title: 'Sign Up',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: '' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'sign-up-confirm': () => import('@/components/BookForDemoConfirmation'),
        'recaptcha': () => import('@/components/ReCaptcha.vue'),
        'header-card-public': () => import('@/components/HeaderPublic'),
        'footer-card': () => import('@/components/Footer'),
        'sign-up-privacy-link': () => import('@/components/SignUpPrivacyLink')
    },
    data () {
        return {
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            rules: {
                required: value => !!value || 'Required.',
                required_number: value => !!value || 'Required. Number only.',
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                },
                cellphone: value => {
                    const pattern = /\d{4}\d{3}\d{4}/
                    return pattern.test(value) || 'Invalid Cellphone no.'
                },
            },
            //static data for faster rendering, this list also exist in dbo.Industries
            industry_list: [
                { id: 1, name: 'Agriculture' },
                { id: 2, name: 'Architecture and Engineering' },
                { id: 3, name: 'Construction' },
                { id: 4, name: 'Education and Training' },
                { id: 5, name: 'E-Commerce' },
                { id: 6, name: 'Energy and Utilities' },
                { id: 7, name: 'Healthcare and Medical Services' },
                { id: 8, name: 'Hospitality (Hotels, Restaurants, Cafes)' },
                { id: 9, name: 'IT, BPO, and Business Services' },
                { id: 10, name: 'Logistics, Transport, and Warehousing' },
                { id: 11, name: 'Manufacturing' },
                { id: 12, name: 'Professional Services (Legal, Accounting, Consulting)' },
                { id: 13, name: 'Real Estate and Property Management' },
                { id: 14, name: 'Retail and Wholesale' },
                { id: 15, name: 'Tourism and Travel' },
                { id: 16, name: 'Non-Profit / NGO' },
                { id: 17, name: 'Other / Not Listed' }
            ],
            intro: {
                line1: `Discover how SweldoNow unifies your time tracking, HR compliance, and payroll into one secure platform. Fill out the form below to book your demo. Our team will contact you within 1 business day to schedule your session.`,
                line2: `See how local enterprises scale operations, automate BIR compliance, and empower teams with self-service workflows.`
            },

            //static data for faster rendering
            sales_partners: [
                {
                    partnerId: "ej",
                    userId: "us0000082o9tssmq40eel",
                    firstName: "Ezra Jeremy",
                    lastName: "Rosero"
                },
                {
                    partnerId: "ashley",
                    userId: "us0000083mgj4wwpbtdbh",
                    firstName: "Joanne Ashley",
                    lastName: "Antonio"
                },
                {
                    partnerId: "jana",
                    userId: "us0000080ujb8hvwtqefz",
                    firstName: "Jana",
                    lastName: "Serrano"
                },
                {
                    partnerId: "kc",
                    userId: "us0000083jfathro7htp3",
                    firstName: "Kearsy",
                    lastName: "Rosete"
                }
            ],

            lead_sources: [
                { id: 1, name: 'Search Engine (Google, Bing)' },
                { id: 2, name: 'AI Search (ChatGPT, Gemini, Copilot, etc)' },
                { id: 3, name: 'Social Media (LinkedIn, Facebook, YouTube)' },
                { id: 4, name: 'Word of Mouth / Referral' },
                { id: 5, name: 'Partner or Consultant' },
                { id: 6, name: 'News, Blog, or Article' },
                { id: 7, name: 'Event or Webinar' },
                { id: 8, name: 'Online Advertisement' },
                { id: 9, name: 'Existing SweldoNow Client' },
                { id: 10, name: 'SweldoNow Knowledge Hub' },
                { id: 11, name: 'Other' },
            ],

            dialogConfirmation: false,
            isNoInternet: false,
            email: null,
            company: null,
            address: null,
            industry: null,
            firstName: null,
            lastName: null,
            position: null,
            cellphone: null,
            employeesCount: null,
            leadSource: null,
            ua_parser: null,
            ua_data: null,
            partnerData: null
        }
    },
    methods: {
        goToHome() {
            this.dialogConfirmation = false
            this.$router.push('/')
        },
        async submitReCaptcha(gresponse) {
            await this.bookNow(gresponse)
        },
        executeRecaptcha() {
            this.error = false
            if (this.$refs.form.validate()) {
                this.loading = true
                this.disabled = true
                this.$refs.recaptcha.execute()
            }
        },
        async bookNow(gresponse) {
            try {
                this.loading = true
                const data = {
                    recaptchaResponse: gresponse,

                    company: this.company,
                    industry: this.industry,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    position: this.position,
                    email: this.email,
                    employeesCount: this.employeesCount,
                    leadSource: this.leadSource,

                    browserName: this.ua_data.browser.name,
                    deviceModel: this.ua_data.device.model,
                    deviceType: this.ua_data.device.type,
                    deviceVendor: this.ua_data.device.vendor,
                    oSName: this.ua_data.os.name,
                    oSVersion: this.ua_data.os.version,
                    partnerUserId: this.partnerData ? this.partnerData.userId : null
                }
                const response = await registrationApi.bookDemo(data)
                if (response && response.status==200) {
                    gtag_report_conversion() //google conversion tracker
                    this.dialogConfirmation = true
                }
                this.loading = false
                this.disabled = false
            }catch(error) {
                if (error.toString().includes("409")) {
                    this.errorMessage = `Email ${this.email} already exist in our database.`
                }            
                else if (error.toString().includes("401") || error.toString().includes("500")) {
                    this.errorMessage = `Error encountered while processing request. If issue persist, contact support@sweldonow.ph`
                }
                else {
                    this.errorMessage = error
                }
                this.error = true
                this.loading = false
                this.disabled = false
            }
        },
        getPartnerId() {
            // 1. Normalize all query keys to lowercase
            const query = Object.keys(this.$route.query).reduce((acc, key) => {
                acc[key.toLowerCase()] = this.$route.query[key]
                return acc
            }, {})

            // 2. Extract partnerid from query (case-insensitive), convert value to lowercase
            let partnerId = query.partnerid ? query.partnerid.toLowerCase() : null

            // 3. If found in URL, save to localStorage with expiry (30 days)
            if (partnerId) {
                localStorage.setItem("partnerId", partnerId)
                localStorage.setItem(
                "partnerIdExpiry",
                Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
                )
                return partnerId
            }

            // 4. If not in URL, check localStorage
            const storedPartnerId = localStorage.getItem("partnerId")
            const expiry = localStorage.getItem("partnerIdExpiry")

            if (storedPartnerId && expiry && Date.now() < expiry) {
                return storedPartnerId.toLowerCase()
            }

            // 5. If expired or missing, clean up
            localStorage.removeItem("partnerId")
            localStorage.removeItem("partnerIdExpiry")
            return null
        }
    },
    created() {
        this.ua_parser = new UAParser(navigator.userAgent)
        this.ua_data = this.ua_parser.getResult()
    },
    mounted() {
        const partnerId = this.getPartnerId()
        if (partnerId) {
            const partnerData = this.sales_partners.filter(x => x.partnerId == partnerId)
            if (partnerData.length) this.partnerData = partnerData[0]
        }
    },
}
</script>
<style scoped>
.content-container {
    background: 
        linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(173, 216, 230, 0.25) 100%),
        url("../assets/landing/landing8.jpg");
    height:auto;
    min-height:600px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}
.form-container {
    margin: 80px 100px 50px 100px;
}
.form-container-mobile {
    margin: 110px 20px 50px 20px;
}
.content1 {
    padding: 0px 20px 0px 20px;
}
.content2 {
    padding: 0px 25px 0px 15px;
}
.content-mobile {
    padding: 0px 25px 0px 20px;
}
.header-intro {
    margin:5px 15px 15px 25px;
}
</style>